import React from 'react';
import { Space, Typography, Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';
import { HowToButton } from '@/components/help/HowToButton';

const { Text, Title } = Typography;

export interface EnterprisePageHeaderProps {
  documentLabel: string;
  documentNumber?: string;
  statusTag?: React.ReactNode;
  subtitle?: React.ReactNode;
  facts?: React.ReactNode;
  primaryActions?: React.ReactNode;
  moreActions?: MenuProps['items'];
  onMoreClick?: (info: { key: string }) => void;
  extra?: React.ReactNode;
  /** Hide the "?" button that opens the ERP Assistant how-to guide for this screen. */
  hideHowTo?: boolean;
}

/**
 * Presentational object header — no business logic.
 */
export const EnterprisePageHeader: React.FC<EnterprisePageHeaderProps> = ({
  documentLabel,
  documentNumber,
  statusTag,
  subtitle,
  facts,
  primaryActions,
  moreActions,
  onMoreClick,
  extra,
  hideHowTo,
}) => {
  const t = EnterpriseTokens.workspace;
  return (
    <div
      style={{
        padding: `clamp(${EnterpriseTokens.spacing.md}px, 2.5vw, ${EnterpriseTokens.spacing.lg}px) clamp(${EnterpriseTokens.spacing.md}px, 3vw, ${t.headerPaddingX}px)`,
        background: t.surfaceHeader,
        borderBottom: t.borderSubtle,
        borderRadius: '8px 8px 0 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: `clamp(${EnterpriseTokens.spacing.sm}px, 2vw, ${EnterpriseTokens.spacing.lg}px)`,
        }}
      >
        <div style={{ flex: '1 1 min(100%, 280px)', minWidth: 0, maxWidth: '100%' }}>
          <Space size={8} wrap align="center">
            <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {documentLabel}
            </Text>
            {documentNumber && (
              <Title level={4} style={{ margin: 0, ...EnterpriseTokens.typography.objectTitle }}>
                {documentNumber}
              </Title>
            )}
            {statusTag}
          </Space>
          {subtitle && (
            <div style={{ marginTop: EnterpriseTokens.spacing.sm, ...EnterpriseTokens.typography.objectSubtitle }}>
              {subtitle}
            </div>
          )}
          {facts && <div style={{ marginTop: EnterpriseTokens.spacing.md }}>{facts}</div>}
        </div>
        {/* Allow shrink + wrap so long document toolbars stay inside the frame */}
        <div style={{ flex: '1 1 min(100%, 360px)', minWidth: 0, maxWidth: '100%' }}>
          <Space wrap size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
            {primaryActions}
            {moreActions && moreActions.length > 0 && (
              <Dropdown menu={{ items: moreActions, onClick: onMoreClick }} trigger={['click']}>
                <Button>More actions</Button>
              </Dropdown>
            )}
            {extra}
            {!hideHowTo && <HowToButton />}
          </Space>
        </div>
      </div>
    </div>
  );
};
